import { useState, ReactNode } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { useToast } from "@/hooks/use-toast";
import { translateMenu } from "@/lib/gemini/client";
import { useUserStore } from "@/store/userStore";
import { cn } from "@/lib/utils";
import { MenuCategory } from "@/types/menu";
import { getLanguageInfo } from "@/lib/languages";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import * as flags from "country-flag-icons/react/3x2";
import { trackEvent } from "@/lib/mixpanel-client";

type CategoryEditDialogProps = {
  category: MenuCategory;
  language: string;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: (updatedCategory: MenuCategory) => void;
  supportedLanguages?: string[];
};

export default function CategoryEditDialog({
  category,
  language,
  isOpen,
  onClose,
  onUpdate,
  supportedLanguages = [],
}: CategoryEditDialogProps) {
  const t = useTranslations("CategoryEditDialog");
  const tLang = useTranslations("LanguageNames");
  const { toast } = useToast();
  const { getRemainingCredits } = useUserStore();
  const remainingCredits = getRemainingCredits();

  const [editedCategory, setEditedCategory] = useState<MenuCategory>({
    ...category,
  });
  const [isSaving, setIsSaving] = useState(false);
  const [isTranslating, setIsTranslating] = useState(false);
  const [shouldTranslate, setShouldTranslate] = useState(false);
  const [initialShouldTranslate] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState<string[]>([]);
  const [initialSelectedLanguages] = useState<string[]>([]);
  const [hasChanges, setHasChanges] = useState(false);

  // Calculate required credits for translations
  const requiredCredits = supportedLanguages.length - 1; // Exclude current language

  // Helper function to get a flag component by country code
  const getFlagComponent = (countryCode: string): ReactNode => {
    const FlagComponent = flags[countryCode as keyof typeof flags];
    return FlagComponent ? (
      <FlagComponent className="w-6 h-4 rounded shadow-sm" />
    ) : null;
  };

  // Helper function to get country code for a language
  const getCountryCode = (languageCode: string): string => {
    // Special cases for Mandarin variants
    if (
      languageCode.toLowerCase() === "chinese_simplified" ||
      languageCode.toLowerCase() === "simplified_chinese" ||
      languageCode.toLowerCase() === "chinese-simplified"
    ) {
      return "CN"; // China flag for Simplified Chinese
    }
    if (
      languageCode.toLowerCase() === "chinese_traditional" ||
      languageCode.toLowerCase() === "traditional_chinese" ||
      languageCode.toLowerCase() === "chinese-traditional"
    ) {
      return "TW"; // Taiwan flag for Traditional Chinese
    }

    const countryMap: Record<string, string> = {
      arabic: "SA",
      bhojpuri: "IN",
      cantonese: "HK",
      chinese: "CN", // Generic Chinese
      chinese_simplified: "CN",
      chinese_traditional: "TW",
      czech: "CZ",
      dutch: "NL",
      english: "GB",
      filipino: "PH",
      french: "FR",
      german: "DE",
      hebrew: "IL",
      hindi: "IN",
      indonesian: "ID",
      italian: "IT",
      japanese: "JP",
      korean: "KR",
      malay: "MY",
      mandarin: "CN", // Generic Mandarin
      mandarin_simplified: "CN",
      mandarin_traditional: "TW",
      persian: "IR",
      polish: "PL",
      portuguese: "PT",
      portuguese_brasil: "BR",
      portuguese_brasileiro: "BR",
      português_brasil: "BR",
      português_brasileiro: "BR",
      romanian: "RO",
      russian: "RU",
      simplified_chinese: "CN",
      slovenian: "SI",
      spanish: "ES",
      swedish: "SE",
      thai: "TH",
      traditional_chinese: "TW",
      turkish: "TR",
      vietnamese: "VN",
    };

    // Special check for Brazilian Portuguese variants
    if (
      languageCode.toLowerCase().includes("portuguese") ||
      languageCode.toLowerCase().includes("português")
    ) {
      if (
        languageCode.toLowerCase().includes("brasil") ||
        languageCode.toLowerCase().includes("brazil") ||
        languageCode.toLowerCase().includes("brasileiro")
      ) {
        return "BR";
      }
    }

    return countryMap[languageCode.toLowerCase()] || "US"; // Default to US flag
  };

  // Get localized language name
  const getLocalizedLanguageName = (code: string): string => {
    // Special cases for languages with variants
    if (code === "chinese-simplified" || code === "chinese_simplified") {
      return tLang("chinese-simplified");
    }
    if (code === "chinese-traditional" || code === "chinese_traditional") {
      return tLang("chinese-traditional");
    }

    // Try to get translation from the LanguageNames namespace
    try {
      return tLang(code.toLowerCase());
    } catch {
      // Fallback to the language name from getLanguageInfo if translation is missing
      return getLanguageInfo(code)?.label || code;
    }
  };

  // Helper function to check if current state is different from initial state
  const checkForChanges = (newCategory: MenuCategory) => {
    const hasNameChange =
      newCategory.name[language] !== category.name[language];

    // Consider translation state changes
    const currentTranslationState =
      shouldTranslate && selectedLanguages.length > 0;
    const initialTranslationState =
      initialShouldTranslate && initialSelectedLanguages.length > 0;
    const hasTranslateChange =
      currentTranslationState !== initialTranslationState;

    // Only consider language selection changes if translations are enabled
    const hasLanguageSelectionChange =
      currentTranslationState &&
      (!initialTranslationState ||
        !arraysHaveSameElements(selectedLanguages, initialSelectedLanguages));

    return hasNameChange || hasTranslateChange || hasLanguageSelectionChange;
  };

  // Helper function to compare arrays regardless of order
  const arraysHaveSameElements = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    const set1 = new Set(arr1);
    return arr2.every((item) => set1.has(item));
  };

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newCategory = {
      ...editedCategory,
      name: { ...editedCategory.name, [language]: e.target.value },
    };
    setEditedCategory(newCategory);
    setHasChanges(checkForChanges(newCategory));
  };

  const handleTranslateToggle = (checked: boolean) => {
    if (checked && remainingCredits === 0) {
      toast({
        variant: "destructive",
        title: t("noCreditsTitle"),
        description: t("noCreditsDescription"),
      });
      return;
    }
    setShouldTranslate(checked);
    if (checked) {
      // When enabling translation, only select languages up to the remaining credits
      const availableLanguages = supportedLanguages.filter(
        (lang) => lang !== language
      );
      const languagesToSelect = availableLanguages.slice(0, remainingCredits);
      setSelectedLanguages(languagesToSelect);
      setHasChanges(checkForChanges(editedCategory));

      // Show a warning if there are more languages available than credits
      if (availableLanguages.length > remainingCredits) {
        toast({
          variant: "default",
          title: t("limitedCreditsTitle"),
          description: t("limitedCreditsDescription", {
            count: remainingCredits,
            plural: remainingCredits === 1 ? "" : "s",
          }),
        });
      }
    } else {
      // When disabling translation, clear selected languages
      setSelectedLanguages([]);
      setHasChanges(checkForChanges(editedCategory));
    }
  };

  const handleSave = async () => {
    if (isSaving) return;
    setIsSaving(true);
    let translationAttempted = false;
    let translationSucceeded = true; // Assume success unless error occurs

    try {
      const updatedCategory = { ...category };

      // Only update the current language's name
      updatedCategory.name = {
        ...category.name, // Preserve existing translations
        [language]: editedCategory.name[language],
      };

      // Check if the name has actually changed
      const hasNameChanged =
        updatedCategory.name[language] !== category.name[language];

      // Track category update
      if (hasNameChanged) {
        trackEvent("Category Updated", {
          Language: language,
        });
      }

      // Only attempt translations if shouldTranslate is true and we have selected languages
      if (
        supportedLanguages.length > 0 &&
        shouldTranslate &&
        selectedLanguages.length > 0 &&
        remainingCredits > 0
      ) {
        translationAttempted = true;
        setIsTranslating(true);

        // Track translation start
        trackEvent("Category Translation Started", {
          "Source Language": language,
          "Target Languages": selectedLanguages,
        });

        // Create a temporary menu with the actual category content
        const tempMenu = {
          id: "temp",
          title: { [language]: updatedCategory.name[language] }, // Use title for context
          categories: [
            {
              ...updatedCategory, // Use the potentially updated category
              name: { [language]: editedCategory.name[language] }, // Only send source lang name
              items: [], // No need to send items for category name translation
            },
          ],
          supportedLanguages: [language],
        };

        for (const targetLang of selectedLanguages) {
          try {
            const translatedMenu = await translateMenu(tempMenu, targetLang);
            const translatedCategory = translatedMenu.categories[0];

            // Only add translations if they have content
            if (translatedCategory.name[targetLang]?.trim()) {
              updatedCategory.name[targetLang] =
                translatedCategory.name[targetLang];
            }
          } catch (error: unknown) {
            translationSucceeded = false;
            console.error(`Translation to ${targetLang} failed:`, error);
            const langLabel = getLanguageInfo(targetLang)?.label || targetLang;
            const errorMessage =
              error instanceof Error
                ? error.message
                : "Unknown translation error";
            toast({
              variant: "destructive",
              title: `Translation Error (${langLabel})`,
              description: errorMessage,
            });

            // Track translation failure
            trackEvent("Category Translation Failed", {
              "Source Language": language,
              "Target Language": targetLang,
              Error: errorMessage,
            });

            // Continue to next language even if one fails
          }
        } // End language loop

        // Track translation completion and credits consumed
        if (translationSucceeded) {
          trackEvent("Category Translation Completed", {
            "Source Language": language,
            "Target Languages": selectedLanguages,
          });

          trackEvent("Credits Consumed", {
            "Credits Used": selectedLanguages.length,
            Feature: "Category Translation",
            Languages: selectedLanguages,
          });
        }

        setIsTranslating(false);
      } // End if shouldTranslate

      // Update local state and close dialog
      onUpdate(updatedCategory);
      onClose();

      // Show appropriate toast message
      if (translationAttempted) {
        if (translationSucceeded) {
          toast({
            title: t("categoryUpdatedAndTranslated"),
            description: t("categoryUpdatedAndTranslatedDescription"),
          });
        } else {
          toast({
            title: t("categoryUpdatedPartial"),
            description: t("categoryUpdatedPartialDescription"),
            variant: "default", // Use default for partial success
          });
        }
      } else {
        toast({
          title: t("categoryUpdated"),
          description: t("categoryUpdatedDescription"),
        });
      }
    } catch (error: unknown) {
      // Catch errors outside translation loop (e.g., onUpdate failure)
      console.error("Error saving category:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to save changes.";
      toast({
        variant: "destructive",
        title: t("saveError"),
        description: t("saveErrorDescription", {
          message: errorMessage,
        }),
      });
    } finally {
      setIsSaving(false);
      setIsTranslating(false); // Ensure translating state is reset
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="p-0 sm:p-6 h-[100dvh] w-[100dvw] sm:h-auto sm:w-auto sm:min-w-[500px] sm:max-w-[600px] sm:max-h-[85vh] flex flex-col overflow-hidden">
        <DialogHeader className="flex-shrink-0 p-6 pb-4 sm:p-0 sm:pb-2">
          <DialogTitle>{t("title")}</DialogTitle>
          <DialogDescription>{t("description")}</DialogDescription>
        </DialogHeader>

        <div className="overflow-y-auto flex-grow py-4 px-6 sm:px-1">
          <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">{t("name")}</Label>
              <Input
                id="name"
                value={editedCategory.name[language] || ""}
                onChange={handleNameChange}
                placeholder={
                  language === "japanese"
                    ? "カテゴリー名"
                    : t("categoryNamePlaceholder")
                }
              />
            </div>

            {supportedLanguages.length > 1 && (
              <div className="space-y-4 pt-4">
                <div className="flex items-center space-x-2">
                  <Switch
                    id="translate"
                    checked={shouldTranslate}
                    onCheckedChange={handleTranslateToggle}
                    disabled={remainingCredits === 0}
                  />
                  <Label
                    htmlFor="translate"
                    className={cn(
                      remainingCredits === 0 ? "text-muted-foreground" : "",
                      "flex-1"
                    )}
                  >
                    {t("translateOtherLanguages")}
                  </Label>
                  {remainingCredits > 0 && (
                    <span className="text-sm text-muted-foreground">
                      {remainingCredits}{" "}
                      {t("credits", { count: remainingCredits })}
                    </span>
                  )}
                </div>
                {remainingCredits === 0 && (
                  <div className="text-sm text-destructive">
                    {t("noCreditsAvailable")}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("purchaseCredits")}
                    </Link>
                  </div>
                )}
                {remainingCredits > 0 && remainingCredits < requiredCredits && (
                  <div className="text-sm text-destructive">
                    {t("notEnoughCredits", { requiredCredits })}{" "}
                    <Link
                      href="/dashboard/settings/plan#purchase-credits"
                      className="underline"
                    >
                      {t("purchaseMoreCredits")}
                    </Link>
                  </div>
                )}
                {shouldTranslate && remainingCredits > 0 && (
                  <div className="space-y-2">
                    <div className="flex items-center justify-between">
                      <Label>{t("selectLanguages")}</Label>
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="select-all-languages"
                          checked={
                            selectedLanguages.length ===
                            supportedLanguages.filter(
                              (lang) => lang !== language
                            ).length
                          }
                          onCheckedChange={(checked) => {
                            if (checked) {
                              const availableLanguages =
                                supportedLanguages.filter(
                                  (lang) => lang !== language
                                );
                              const languagesToSelect =
                                availableLanguages.slice(
                                  0,
                                  Math.min(
                                    availableLanguages.length,
                                    remainingCredits
                                  )
                                );
                              setSelectedLanguages(languagesToSelect);
                              setHasChanges(checkForChanges(editedCategory));
                              if (
                                availableLanguages.length > remainingCredits
                              ) {
                                toast({
                                  variant: "default",
                                  title: t("limitedCreditsTitle"),
                                  description: t("limitedCreditsDescription", {
                                    count: remainingCredits,
                                    plural: remainingCredits === 1 ? "" : "s",
                                  }),
                                });
                              }
                            } else {
                              setSelectedLanguages([]);
                              setHasChanges(checkForChanges(editedCategory));
                            }
                          }}
                        />
                        <Label
                          htmlFor="select-all-languages"
                          className="text-sm"
                        >
                          {t("selectAll")}
                        </Label>
                      </div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {supportedLanguages.map((lang) => {
                        if (lang === language) return null;
                        const langInfo = getLanguageInfo(lang);
                        const isDisabled =
                          !selectedLanguages.includes(lang) &&
                          selectedLanguages.length >= remainingCredits;

                        return (
                          <div
                            key={lang}
                            className={cn(
                              "flex items-center p-2 px-4 bg-muted/50 rounded-md",
                              isDisabled && "opacity-50"
                            )}
                          >
                            <Checkbox
                              id={`lang-${lang}`}
                              checked={selectedLanguages.includes(lang)}
                              onCheckedChange={(checked) => {
                                if (checked) {
                                  if (
                                    selectedLanguages.length + 1 >
                                    remainingCredits
                                  ) {
                                    toast({
                                      variant: "destructive",
                                      title: t("insufficientCredits"),
                                      description: t("notEnoughCreditsMessage"),
                                    });
                                    return;
                                  }
                                  const newSelectedLanguages = [
                                    ...selectedLanguages,
                                    lang,
                                  ];
                                  setSelectedLanguages(newSelectedLanguages);
                                  setHasChanges(
                                    checkForChanges(editedCategory)
                                  );
                                } else {
                                  const newSelectedLanguages =
                                    selectedLanguages.filter((l) => l !== lang);
                                  setSelectedLanguages(newSelectedLanguages);
                                  setHasChanges(
                                    checkForChanges(editedCategory)
                                  );
                                }
                              }}
                              disabled={isDisabled}
                              className="mr-2"
                            />
                            <div className="flex-shrink-0 mx-2">
                              {getFlagComponent(getCountryCode(lang))}
                            </div>
                            <div className="flex flex-col flex-1 ml-2">
                              <Label
                                htmlFor={`lang-${lang}`}
                                className={cn(
                                  "text-sm font-medium",
                                  isDisabled && "text-muted-foreground"
                                )}
                              >
                                {langInfo?.nativeName}
                              </Label>
                              <span className="text-xs text-muted-foreground">
                                {getLocalizedLanguageName(lang)}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0 p-6 pt-4 sm:p-0 mt-auto border-t sm:border-0">
          <div className="flex gap-2 w-full sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 sm:flex-none"
            >
              {t("cancel")}
            </Button>
            <Button
              type="submit"
              onClick={handleSave}
              disabled={isSaving || !hasChanges}
              className="flex-1 sm:flex-none"
            >
              {isSaving ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 border-2 border-background/30 border-t-background rounded-full animate-spin" />
                  <span>
                    {isTranslating ? t("updatingTranslations") : t("updating")}
                  </span>
                </div>
              ) : (
                t("update")
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
